import type { SeatReservation as SdkSeatReservation } from "@colyseus/sdk";
import type { SeatReservation } from "@wr/contracts";
import type { GameRoomStateView, LobbyStateView } from "@wr/game-core";
import { t } from "@wr/i18n";

/**
 * Connecting the browser to the Colyseus process that owns the room.
 *
 * ── LOCKED: the client SDK is `@colyseus/sdk`, NOT `colyseus.js` (§16 A6) ────
 * `colyseus.js` caps at 0.16 / Schema v3 and cannot talk to our 0.17 server.
 *
 * ── T-373 P1: the SDK is loaded at the join seam, not at module load ─────────
 * The `Client` class is pulled in with a **dynamic** `import("@colyseus/sdk")`
 * inside the two join functions rather than a static top-level import. Only its
 * *type* is imported statically (erased at build). This module is reached from
 * an eager app provider (`SeatProvider` → `useGameRoom`), so a static value
 * import here would drag the ≈100 KB SDK into the first-paint chunk that the
 * splash/login/signup path parses — none of which has a socket. Deferring the
 * value import moves it into its own chunk that loads only when a player actually
 * joins a lobby or game room (post-login), the same lazy-at-use pattern the game
 * engine already uses for its board. `endpointForReservation` and the reconnect
 * helpers are pure/type-only and stay static.
 *
 * ── LOCKED: the reservation goes in verbatim (§16 A8, A11) ──────────────────
 * `consumeSeatReservation(reservation)` receives the exact object the api
 * returned. The SDK reads `publicAddress`, `processId`, `roomId` and
 * `sessionId` off it and builds
 *   wss://<publicAddress>/<processId>/<roomId>?sessionId=…
 * which — because `publicAddress` is `"<host>/<owning process port>"` — is the
 * one URL that reaches the right process through the single public hostname
 * (architecture §3.2, infra-reference §1). Reshape the object and the socket
 * goes somewhere else, or nowhere.
 */

/**
 * Derive the SDK endpoint from the reservation itself.
 *
 * The `Client` constructor only supplies two things once a reservation is in
 * hand: the URL *scheme* (`secure` → `wss://` vs `ws://`) and a fallback host
 * that `buildEndpoint` never reaches while `publicAddress` is set. So:
 *
 *  - host: from `reservation.publicAddress`. Never configured, never guessed.
 *    There is deliberately no `VITE_GAME_URL` in this app.
 *  - scheme: from the page. A page served over https gets `wss://`; local dev
 *    over http gets `ws://`. Both services are behind the same edge in every
 *    real deployment, so the page's scheme is the correct answer and a
 *    mixed-content socket is impossible by construction.
 */
export function endpointForReservation(
  reservation: SeatReservation,
  pageProtocol: string = typeof window === "undefined" ? "https:" : window.location.protocol,
): string {
  const publicAddress = reservation.publicAddress;
  if (typeof publicAddress !== "string" || publicAddress.length === 0) {
    throw new Error(t("error.missingPublicAddress"));
  }
  const scheme = pageProtocol === "https:" ? "https" : "http";
  return `${scheme}://${publicAddress}`;
}

/**
 * ── How every join in this file works, and the two rules it keeps ───────────
 *
 * No `rootSchema` argument anywhere: Colyseus 0.17 ships the schema definition
 * over the handshake and the SDK reconstructs the state by reflection. That is
 * the path the Sprint 0 spike proved, and it keeps a second copy of
 * `@colyseus/schema` out of this bundle (the SDK brings the only one the browser
 * is allowed to have, and ESLint enforces it). Each state is typed with the
 * shared view from `@wr/game-core`, which `apps/game` asserts its `Schema`
 * classes against at compile time in both directions (LOCKED rule 17).
 *
 * ── The access token (S2-4) ─────────────────────────────────────────────────
 *
 * Setting `client.http.authToken` is what makes the SDK append `_authToken` to
 * the WebSocket URL, which the server reads into `AuthContext.token` and
 * `AuthenticatedRoom.onAuth` verifies against the API's JWKS. It is the ONLY
 * thing the client contributes to its own identity, and it is not self-asserted:
 * it is a 5-minute EdDSA token the API minted for the session cookie.
 *
 * The token is passed per join and never cached here — it expires in five
 * minutes, so a stale one is a failed join for no reason. Every caller fetches a
 * fresh one immediately before every join, including every re-join after a lost
 * seat, which is what makes an expired token get refused at re-join too.
 *
 * `joinReservedRoom` (the S1 skeleton's join, typed `PingStateView`) was deleted
 * here by §16f **A60** along with the room it reached; `joinLobbyRoom` and
 * `joinGameRoom` are the two that were ever used by the product.
 */
export type LobbyRoomConnection = Awaited<ReturnType<typeof joinLobbyRoom>>;

/**
 * Consume a **lobby** seat reservation and return the live room (S3-4).
 *
 * The mechanics described in the header, with one difference worth naming: the
 * state is typed with `LobbyStateView`, the hand-written view that `apps/game`
 * asserts its `Schema` classes against in both directions.
 *
 * **The cast is type-level only, on the SAME reference** — nothing is copied,
 * spread or re-keyed (§16 A8, A11). `@wr/contracts` types `name` as optional
 * because its schema is deliberately loose (it must never strip a field Colyseus
 * adds); the SDK's `ISeatReservation` types it as required. Same runtime object,
 * two views of it. Reconciling by *building* an object here is precisely what A8
 * forbids, so we reconcile the type instead.
 */
export async function joinLobbyRoom(reservation: SeatReservation, accessToken: string) {
  const { Client } = await import("@colyseus/sdk");
  const client = new Client(endpointForReservation(reservation));
  client.http.authToken = accessToken;
  return client.consumeSeatReservation<LobbyStateView>(
    reservation as unknown as SdkSeatReservation,
  );
}

export type GameRoomConnection = Awaited<ReturnType<typeof joinGameRoom>>;

/**
 * Consume a **game-room** seat reservation and return the live room (S4-5).
 *
 * The reservation comes from `POST /api/v1/rooms/join`, unwrapped once by
 * `postRoomJoin`, and arrives here as the object the api returned — same
 * reference, same keys (§16 A8/A11). Identical mechanics to the other two joins;
 * the only difference is the state view, `GameRoomStateView`, which `apps/game`
 * asserts its `Schema` classes against in both directions (LOCKED rule 17).
 *
 * There is no room *controller* here on purpose: S4-7 owns the room screen and
 * the hook that drives it, and a controller written a ticket ahead of its screen
 * is a controller written against a guess.
 */
export async function joinGameRoom(reservation: SeatReservation, accessToken: string) {
  const { Client } = await import("@colyseus/sdk");
  const client = new Client(endpointForReservation(reservation));
  client.http.authToken = accessToken;
  const room = await client.consumeSeatReservation<GameRoomStateView>(
    reservation as unknown as SdkSeatReservation,
  );
  allowEarlyReconnect(room);
  return room;
}

/**
 * **Q11 (Quinn, T-224) — a drop in the first five seconds must retry like any
 * other drop.**
 *
 * `@colyseus/sdk`'s `Room` ships `reconnection.minUptime = 5000` and
 * `handleReconnection` reads it *before* anything else:
 *
 * ```
 * if (Date.now() - this.joinedAtTime < this.reconnection.minUptime) {
 *   this.onLeave.invoke(CloseCode.ABNORMAL_CLOSURE, "Room uptime too short…");
 *   return;                       // ← no attempt is ever made
 * }
 * ```
 *
 * So a socket that dies at t+2 s produces **zero** reconnection attempts and goes
 * straight to `onLeave`, which in this client is a lost seat and a **T3** panel:
 * *"your 25 is gone, it counts as a loss"* — rendered ~4 s after the drop while
 * **the server is still holding the seat for the full 45-second grace window**.
 * Ivy's §16 item 7 states the design position exactly: *"T3 must not render while
 * the server may still be holding the seat. The fix is in the reconnection layer,
 * not in a copy change — a softer T3 would be a worse answer than a correct one."*
 *
 * **Why zero and not a smaller number.** `minUptime` exists to stop a client
 * hammering a room it can never stay in; that hazard is already bounded by
 * `maxRetries` (15) and the exponential backoff, and it is bounded again by the
 * server, which closes the seat when the grace window expires and answers a late
 * reconnect with a real refusal. Any non-zero floor is a window in which the
 * client tells a player their money is gone while the server disagrees, and there
 * is no length of that window which is honest.
 *
 * **It is the game room only.** A drop on the lobby socket costs a list that
 * re-fetches; a drop on this one costs a seat with an entry fee in it. The
 * asymmetry is the point, so the call sits here rather than in a shared helper.
 *
 * The value is set on the room the SDK just built and before any listener is
 * attached, so no drop can be observed under the old floor.
 */
function allowEarlyReconnect(room: { reconnection: { minUptime: number } }): void {
  room.reconnection.minUptime = 0;
}

/**
 * `CloseCode.MAY_TRY_RECONNECT` — the one close code the SDK reads as *"this
 * socket is finished, the session is not"*.
 *
 * Stated as a number rather than imported: it lives in `@colyseus/shared-types`,
 * which is a transitive dependency of the SDK and **not** a dependency of this
 * app (§16 A6 pins exactly one Colyseus package here). A phantom import would
 * resolve in the monorepo and break a clean install.
 */
const MAY_TRY_RECONNECT = 4010;

/**
 * **Restart the SDK's reconnection attempt sequence — the banner's `Retry`,
 * S2 §20.1's LOCKED behaviour clause** (*"a press while an attempt is in flight
 * restarts the attempt sequence"*).
 *
 * Two operations, and the order matters:
 *
 * 1. `reconnection.retryCount = 0` — the sequence stops **dying**. The SDK gives
 *    up at `maxRetries` (15) and then invokes `onLeave`, which in this client is
 *    a lost seat and a terminal panel. A press means the player is still asking,
 *    so the count goes back to the bottom of the curve — which also puts the next
 *    attempt 200ms away instead of at the 5s ceiling the backoff has climbed to.
 * 2. `connection.close(MAY_TRY_RECONNECT)` — the sequence stops **stalling**.
 *    This is the operation that matters and it is not obvious: the SDK only
 *    schedules its next attempt from the *current* socket's `onclose`, so a
 *    handshake that **hangs** (a bad network rather than a refused one — the
 *    shape Quinn measured on staging in T-222) produces no close event and
 *    therefore no further attempts, ever. Closing with this code fires the SDK's
 *    own `onclose` path synchronously — `onDrop` → `handleReconnection` →
 *    `retryReconnection` — against a socket it has stopped waiting for.
 *
 * Both fields are public API on the SDK's `Room`. Nothing here re-implements the
 * reconnect: the token, the URL and the attempt itself stay the SDK's, which is
 * why this cannot drift from what its own automatic retries do.
 *
 * **The mechanism is pinned against a real server** in
 * `apps/game/src/rooms/rule18-reconnect.test.ts` (*"a stalled reconnection
 * sequence"*): a hung handshake, a held seat, and this call as the only thing
 * that gets the session back. If an SDK upgrade changes either field, that test
 * fails instead of the product quietly losing its only mid-game recovery.
 *
 * Returns `false` when there is no socket left to restart (the SDK already gave
 * up and disposed the room object); the caller decides what to do instead — it
 * must not present this as the recovery when it cannot be one (§20.4: *"a button
 * that has failed is no longer an affordance, it is furniture"*).
 */
export function restartReconnect(room: GameRoomConnection): boolean {
  const socket = room.connection;
  if (!socket) return false;
  room.reconnection.retryCount = 0;
  try {
    socket.close(MAY_TRY_RECONNECT);
  } catch {
    // A transport with no live socket at all. Nothing was restarted, and the
    // caller is told so rather than shown a button that did nothing.
    return false;
  }
  return true;
}

/**
 * **The consented leave, said only while there is a socket to say it on** —
 * T-244 finding N3 / S6 NB4.
 *
 * ── What was logged, and why it is a teardown-ordering bug ──────────────────
 *
 * `POST /api/v1/rooms/:id/leave` is authoritative for the money and, through
 * `releaseSeat`, closes this client with **4000 (consented)** server-side. The
 * screen then navigates, `SeatProvider` tears the controller down, and the
 * teardown called `room.leave(true)` on a socket the server had already closed.
 * The SDK's `leave()` writes the LEAVE_ROOM frame with no liveness check —
 * unlike its own `ping()`, which guards on `connection.isOpen` — so
 * `WebSocket.send()` ran against a CLOSED socket and every browser logs
 * `WebSocket is already in CLOSING or CLOSED state.` Nothing user-visible; the
 * leave had already completed. But a console error on the ordinary leave path is
 * noise that hides the next real one.
 *
 * There is a second, quieter defect in the same line: `leave()`'s promise
 * resolves **on `onLeave`**, and a socket that has already fired `onLeave` will
 * never fire it again — so `await room.leave(true)` on a dead socket never
 * settles. `enter()` awaits its `detach()`, so a stale dead room could have
 * stalled the next join indefinitely.
 *
 * ── What this does, and what it deliberately does not change ────────────────
 *
 * **Leave semantics are untouched.** While the socket is open this is exactly
 * the call that was made before, consented flag and all — LOCKED rule 18's
 * in-app navigation still frees the seat immediately, and a closed tab is still
 * a drop that holds its seat for the whole grace window. When the socket is
 * already gone there is no seat left to consent about: the frame cannot be
 * delivered, so `leave(false)` closes the transport instead — a no-op on an
 * already-closed socket, and the thing that stops the SDK's own reconnection
 * sequence from retrying a room we are done with. It is not awaited, for the
 * never-settling reason above.
 */
export async function leaveConsented(
  room: LobbyRoomConnection | GameRoomConnection,
): Promise<void> {
  if (room.connection?.isOpen === true) {
    try {
      await room.leave(true);
    } catch {
      // The socket died between the check and the frame. The outcome the caller
      // wanted has happened either way.
    }
    return;
  }
  void room.leave(false).catch(() => undefined);
}
